import React, {useState} from "react"
import {useDispatch} from "react-redux"
import {useNavigate} from "react-router-dom"
import {Alert, AlertIcon, Button, ButtonGroup} from "@chakra-ui/react"
import {ChevronLeftIcon, ChevronRightIcon} from "@heroicons/react/24/outline"
import {useForm} from "react-hook-form"

import {Card, Section, VerticalSteps} from "@components/index"
import {useSteps} from "@components/nav/useStep"
import {IGame, ILabelSet, IQuestion, QuestionField, TGameModes, VoteButtonType} from "@backend/types"
import {voteButtonsDefault} from "@backend/constants"
import {PickedUser} from "@modules/user/components/UserSelect"
import {PickedGroup} from "@modules/user/components/GroupSelect"
import {LabelSetSelect, LabelSetTable} from "@modules/label/components"
import {FieldType} from "@components/types"


import {PlayButtonsEditor} from "./PlayButtonsEditor"
import {gameModeOptions} from "../../constants"
import {GameAccess, GameInfo, GameMode} from "../forms"
import {gameActions} from "../../service"


const fields: FieldType = {
    accessType: {
        parentField: "accessOptions",
        field: "accessType",
        rules: {
            required: "Please select a user access option"
        }
    },
    mode: {
        field: "mode",
        parentField: "modeOptions",
        initialValue: gameModeOptions.basic.value,
        rules: {
            required: "Please select a game mode"
        }
    }
}

type StepType = {
    title: string,
    triggers?: string[],
    subValidateField?: {
        parentCheck?: (getValues: (name: string) => string) => boolean
        fields: string[],
        message: string
    },
    hideOnModes?: TGameModes[]
}

const steps: StepType[] = [
    {
        title: "Appearance",
        triggers: ["info.icon", "info.color", "info.title", "info.description"]
    },
    {
        title: "Access",
        triggers: ["accessOptions.accessType"],
        subValidateField: {
            parentCheck: (getValues) => ["privateToUsers", "privateToGroups"].includes(getValues("accessOptions.accessType")),
            fields: ["users", "groups"],
            message: "Please add users or groups"
        }
    },
    {
        title: "Questions",
        triggers: ["modeOptions.mode"],
        subValidateField: {
            fields: ["questions"],
            message: "Please add texts"
        }
    },
    {
        title: "Labels",
        subValidateField: {
            fields: ["labelSet"],
            message: "Please select a label set"
        },
        hideOnModes: ["battle"]
    },
    {
        title: "Buttons",
        subValidateField: {
            fields: ["voteButtons"],
            message: "Please add a vote button"
        },
        hideOnModes: ["battle"]
    }
]

type GameCreateFormProps = {
    game?: IGame,
    onFinish: () => void
}

export function GameCreateForm({game, onFinish}: GameCreateFormProps) {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const {
        stepProps,
        isActiveStep,
        goToStep,
        complete,
        completeAndGoNext,
        getStatus,
        goToPrevious,
        setStatus
    } = useSteps({
        index: 0,
        count: steps.length,
    })

    const [selectedItems, setSelectedItems] = useState<{
        labelSet: ILabelSet | null
        users: PickedUser[] | null
        groups: PickedGroup[] | null
        questions: IQuestion[] | null
        questionFields: QuestionField[] | null
        questionRefIdField: string | null
        voteButtons: VoteButtonType[] | null
    }>({
        labelSet: game?.labelOptions?.setId as ILabelSet, //null, //game?.labelSetId as ILabelSet || null,
        users: game?.accessOptions.userIds as unknown as PickedUser[] || [],
        groups: game?.accessOptions.groupIds as PickedGroup[] || [],
        questionFields: game?.modeOptions.fields || [],
        questionRefIdField: game?.modeOptions.questionRefIdField || null,
        questions: game?.modeOptions.questionIds as IQuestion[] || [],//game?.questionIds as IQuestion[] || [],
        voteButtons: game?.voteOptions.buttons || Object.values(voteButtonsDefault),// game?.voteButtons as VoteButtonType[] || []
    })

    const {
        handleSubmit,
        register,
        setValue,
        clearErrors,
        trigger,
        control,
        setError,
        getValues,
        formState: {
            errors,
            isSubmitting
        }
    } = useForm({
        defaultValues: {
            info: game?.info,
            accessOptions: {
                accessType: game?.accessOptions?.accessType
            },
            modeOptions: {
                mode: game?.modeOptions?.mode || gameModeOptions.basic.value
            }
        }
    })


    async function onSubmit(values) {

        const isValid = await checkValidation()

        if (!isValid)
            return false

        const {questions, questionFields, questionRefIdField, users, groups, labelSet, voteButtons} = selectedItems

        const {info, accessOptions, modeOptions} = values

        const gameData: IGame = {
            info,
            accessOptions: {
                accessType: accessOptions.accessType,
                userIds: (users || []).map(x => x._id),
                groupIds: (groups || []).map(x => x._id),
            },
            modeOptions: {
                mode: modeOptions.mode,
                fields: questionFields,
                questionRefIdField,
                questionIds: questions
            }
        }

        if (modeOptions.mode !== "battle") {
            gameData.labelOptions = {
                setId: labelSet._id
            }
            gameData.voteOptions = {
                buttons: voteButtons
            }
        }

        try {
            if (game?._id) {
                await gameActions.actions.update(game._id, gameData)(dispatch)
            } else {
                await gameActions.actions.create(gameData)(dispatch)
            }
            navigate("../")
        } catch (err) {
            alert(err.message)
        }

    }

    function onSelectedChange(props: {
        labelSet?: ILabelSet,
        questionFields?: QuestionField[],
        questions?: IQuestion[],
        users?: PickedUser[],
        groups?: PickedGroup[],
        voteButtons?: VoteButtonType[]
    } = {}) {
        // @ts-ignore
        clearErrors()

        setSelectedItems({
            ...selectedItems,
            ...props
        })
        complete()
    }

    function onLabelSetRemove() {
        setSelectedItems(prevState => ({
            ...prevState,
            labelSet: undefined
        }))
    }

    async function checkValidation(): Promise<Boolean> {
        const {triggers, subValidateField} = steps[stepProps.activeStep]
        let isValid = true

        if (triggers) {
            // @ts-ignore
            isValid = await trigger(triggers)
        }

        if (!isValid) {
            setStatus(stepProps.activeStep, "error")
            return false
        }

        if (subValidateField && (!subValidateField.parentCheck || (subValidateField.parentCheck && subValidateField.parentCheck(getValues)))) {
            isValid = subValidateField.fields.some(key => Array.isArray(selectedItems[key]) ? (selectedItems[key] || []).length > 0 : selectedItems[key])
        }

        if (!isValid) {
            setStatus(stepProps.activeStep, "error")
            if (subValidateField?.fields) {
                // @ts-ignore
                setError(subValidateField.fields[0], {
                    type: "required",
                    message: subValidateField.message
                })
            }
        } else {
            if (subValidateField?.fields) {
                // @ts-ignore
                clearErrors(subValidateField.fields[0])
            }
        }

        return isValid

    }

    async function onNext() {

        const isValid = await checkValidation()
        if (isValid)
            completeAndGoNext()
    }


    function onUserAccessClick(field, value) {

        // @ts-ignore
        setValue(field, value)

        setSelectedItems({
            ...selectedItems,
            users: [],
            groups: []
        })

        complete()

    }

    function onGameModeClick(field, value) {
        setSelectedItems({
            ...selectedItems,
            questions: []
        })
        // @ts-ignore
        setValue(field, value)
    }


    return <Card
        title={game ? `Edit ${game.info.title}` : "Create a New Game"}
        footer={<div className="flex flex-col gap-y-6">
            {Object.keys(errors).length > 0 &&
              <Alert status="error">
                <AlertIcon/>
                <ul>
                    {Object.keys(errors).reduce((acc, key) => {
                        if (errors[key].message)
                            acc.push(errors[key])
                        else
                            acc.push(...Object.values(errors[key]))
                        return acc
                    }, [])
                        .map((x, i) =>
                            <li key={i}>
                                {x.message as string}
                            </li>)}
                </ul>

              </Alert>}
            <div className="flex items-center justify-end">
                <ButtonGroup variant="outline" spacing="6">
                    {stepProps.activeStep > 0 &&
                      <Button onClick={goToPrevious}
                              leftIcon={<ChevronLeftIcon className="w-4 h-4"/>}>Previous</Button>}

                    {(stepProps.activeStep === steps.length - 1 || getValues("modeOptions.mode") === "battle" && stepProps.activeStep === 2) ?
                        <Button type="submit"
                                isLoading={isSubmitting}
                                onClick={handleSubmit(onSubmit)} colorScheme="blue">Save</Button> :
                        <Button onClick={onNext}
                                rightIcon={<ChevronRightIcon className="w-4 h-4"/>}>Next</Button>
                    }
                </ButtonGroup>
            </div>
        </div>}
    >
        <div className="grid grid-cols-4 gap-6">
            <div className="col-span-1">
                <nav className="flex justify-start" aria-label="Progress">
                    <ol role="list" className="space-y-6">
                        {steps.filter(step => !step.hideOnModes || !step.hideOnModes.includes(getValues("modeOptions.mode"))).map((step, stepIndex) => (
                            <li key={stepIndex}>
                                <VerticalSteps
                                    title={step.title}
                                    stepStatus={getStatus(stepIndex)}
                                    onClick={goToStep.bind(null, stepIndex)}
                                />
                            </li>
                        ))}
                    </ol>
                </nav>
            </div>
            <div className="col-span-3">
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Section hidden={!isActiveStep(0)}>
                        <GameInfo
                            register={register}
                            errors={errors}
                            setValue={setValue}
                            control={control}
                        />
                    </Section>
                </form>
                <Section hidden={!isActiveStep(1)}>
                    <GameAccess
                        register={register}
                        onSelect={onUserAccessClick}
                        onSelectedChange={onSelectedChange}
                        control={control}
                        users={selectedItems.users}
                        groups={selectedItems.groups}
                        {...fields.accessType}
                    />
                </Section>
                <Section hidden={!isActiveStep(2)}>
                    <GameMode
                        register={register}
                        errors={errors}
                        onSelect={onGameModeClick}
                        onSelectedChange={onSelectedChange}
                        control={control}
                        setValue={setValue}
                        questionFields={selectedItems.questionFields}
                        questions={selectedItems.questions}
                        questionRefIdField=""
                        {...fields.mode}
                    />
                </Section>
                <Section hidden={!isActiveStep(3) || getValues("modeOptions.mode") === "battle"}>
                    <div className="col-span-full">
                        {selectedItems.labelSet ?
                            <LabelSetTable
                                labelSet={selectedItems.labelSet}
                                onLabelSetRemove={onLabelSetRemove}/> :
                            <LabelSetSelect
                                selectedLabelSet={selectedItems.labelSet}
                                onLabelSetChange={onSelectedChange}
                            />
                        }
                    </div>
                </Section>

                <Section hidden={!isActiveStep(4) || getValues("modeOptions.mode") === "battle"}>
                    <div className="col-span-full">
                        <PlayButtonsEditor voteButtons={selectedItems.voteButtons} onSelectedChange={onSelectedChange}/>
                    </div>
                </Section>
            </div>
        </div>
    </Card>
}